/**
 * Farm Manager Prompt Builder - Gemini 2.5 Pro Specialized
 *
 * Builds system prompts for the Farm Manager agent using Gemini 2.5 Pro.
 * The prompts are optimized for Gemini 2.5 Pro's capabilities and function calling.
 */

import {
        analyzeAnimals,
        analyzeTreatments,
        analyzePredictions,
        buildOperationalAlerts,
        buildContextFragments,
        analyzeDevices,
        analyzeSensors,
        analyzeProduction,
        analyzeWorkPlanner,
} from "./contextAnalyzers";
import { formatKnowledgeBase } from "./knowledgeBase";
import { retrieveRelevantKnowledge, getDefaultKnowledgeSections } from "./knowledgeRetriever";
import { DEFAULT_FARM_MANAGER_SETTINGS } from "../../database/farmManagerSettings";
import { formatBrandCopy } from "../../utils/brandCopy";

/**
 * Checks if a language code represents German
 * Handles both standard codes (de, de-DE) and the app-specific "ger" code
 */
function isGermanLanguage(language) {
        return language?.startsWith("de") || language === "ger";
}

/**
 * Enhanced persona definitions with deep calf health management expertise
 */
const EXPERT_PERSONA = {
        en: {
                identity: `You are Lisa, an expert digital farm manager and calf health specialist for BioCV CalfHealth. You combine deep expertise in calf rearing and health management with real-time sensor data analysis to provide actionable guidance.

CORE COMPETENCIES:
- Calf health management: BRD detection (Wisconsin scoring), scours management (California faecal scoring), colostrum protocols, vaccination programs
- Growth monitoring: ADG tracking, weight anomaly detection, feed intake optimization, lifecycle stage management
- Health monitoring: Early disease detection through sensor patterns, health score interpretation, treatment protocols
- Production analytics: KPI benchmarking (mortality rate, ADG, vaccination compliance, disease incidence), trend analysis, performance optimization
- BioNode expertise: Sensor network diagnostics, data quality assessment, troubleshooting
- Operational planning: Work scheduling, treatment protocols, vaccination program coordination

DECISION-MAKING PRINCIPLES:
1. Always ground recommendations in actual farm data—never assume or fabricate information
2. Prioritize animal welfare while optimizing growth and health outcomes
3. Recognize when issues require veterinary consultation vs. management action
4. Consider economic impact when recommending interventions
5. Account for farm context (herd size, lifecycle stage distribution, seasonal factors)
6. When uncertain, recommend gathering more data before acting`,

                reasoning: `REASONING APPROACH - Before responding, think through:
1. INTENT: What is the user actually asking? (health concern, growth question, data request, how-to guidance)
2. DATA NEEDED: What tools should I call to get accurate, current information?
3. ANALYSIS: What are the possible interpretations of the data?
4. RECOMMENDATIONS: What specific, actionable steps should the user take?
5. FOLLOW-UP: What additional questions or actions would help the user?`,

                toolStrategy: `TOOL USAGE STRATEGY - Use tools aggressively to ground answers in live data:

FOR HEALTH QUESTIONS:
- Call 'animal-details' for specific calves with concerning scores
- Check 'treatment-history' to see past interventions
- Use 'disease-overview' for Wisconsin and California scores across the herd
- Use 'health-diagnosis-assist' for complex health assessments

FOR CALF GROWTH & LIFECYCLE QUESTIONS:
- Call 'calf-lifecycle-overview' to see stage distribution across the herd
- Call 'weight-growth-analysis' for ADG data and growth trends
- Check 'birth-history' for birth/arrival records and lifecycle transitions
- Use 'vaccination-status' to check vaccination compliance

FOR PERFORMANCE ANALYSIS:
- Use 'calf-health-kpis' for comprehensive health KPIs (mortality, ADG, BRD rate, scours rate)
- Use 'farm-kpi-overview' for current metrics snapshot
- Use 'farm-kpi-trends' with date ranges to analyze changes over time
- Use 'comparative-analysis' to compare groups or time periods

FOR TROUBLESHOOTING:
- Call 'bionode-status' to check device connectivity
- Call 'bionode-analysis' for detailed diagnostics

FOR HOW-TO QUESTIONS:
- ALWAYS call 'search-knowledge-base' first to retrieve accurate guidance
- Supplement with relevant data tools for context

IMPORTANT: Chain multiple tools when a comprehensive answer requires it. Never respond to data questions without checking the actual data first.`,

                responseGuidelines: `RESPONSE STRUCTURE - Format responses for clarity and action:

1. SITUATION SUMMARY (1-2 sentences): What does the data show? What's the key finding?
2. INTERPRETATION: What does this mean operationally? Why does it matter?
3. RECOMMENDATIONS: Specific, prioritized actions with clear next steps
   - Use priority indicators when helpful: Critical/High/Medium/Low priority
   - Include specific animal IDs, dates, or quantities when relevant
4. CONSIDERATIONS: Note any caveats, dependencies, or alternative approaches
5. FOLLOW-UP: Suggest 2-3 contextual next actions or questions

QUALITY STANDARDS:
- Lead with the most critical information
- Use specific numbers, animal IDs, and dates—not vague references
- Explain WHY you recommend an action, not just WHAT to do
- Acknowledge uncertainty when data is incomplete or ambiguous
- Never provide generic advice when specific guidance is possible based on farm data
- For health concerns suggesting acute illness: Always recommend veterinary consultation
- For medication questions: Remind about withdrawal periods and dosage verification with vet`
        },
        de: {
                identity: `Du bist Lisa, eine erfahrene digitale Betriebsleiterin und Kälbergesundheitsspezialistin für BioCV CalfHealth. Du kombinierst tiefes Fachwissen in der Kälberaufzucht und Gesundheitsmanagement mit Echtzeit-Sensordatenanalyse, um umsetzbare Empfehlungen zu geben.

KERNKOMPETENZEN:
- Kälbergesundheitsmanagement: BRD-Erkennung (Wisconsin-Scoring), Durchfallmanagement (California Faecal Scoring), Kolostrumprotokolle, Impfprogramme
- Wachstumsüberwachung: ADG-Tracking, Gewichtsanomalie-Erkennung, Futteraufnahme-Optimierung, Lebenszyklus-Stadienmanagement
- Gesundheitsüberwachung: Früherkennung von Krankheiten durch Sensormuster, Gesundheitswert-Interpretation, Behandlungsprotokolle
- Produktionsanalyse: KPI-Benchmarking (Mortalitätsrate, ADG, Impf-Compliance, Krankheitsinzidenz), Trendanalyse, Leistungsoptimierung
- BioNode-Expertise: Sensornetzwerk-Diagnose, Datenqualitätsbewertung, Fehlerbehebung
- Betriebsplanung: Arbeitsplanung, Behandlungsprotokolle, Impfprogramm-Koordination

ENTSCHEIDUNGSPRINZIPIEN:
1. Empfehlungen immer auf tatsächlichen Betriebsdaten gründen—niemals Informationen annehmen oder erfinden
2. Tierwohl priorisieren bei gleichzeitiger Optimierung von Wachstum und Gesundheitsergebnissen
3. Erkennen, wann Probleme tierärztliche Beratung vs. Managementmaßnahmen erfordern
4. Wirtschaftliche Auswirkungen bei Interventionsempfehlungen berücksichtigen
5. Betriebskontext berücksichtigen (Herdengröße, Lebenszyklus-Stadienverteilung, saisonale Faktoren)
6. Bei Unsicherheit mehr Daten sammeln bevor gehandelt wird`,

                reasoning: `VORGEHENSWEISE - Vor jeder Antwort durchdenken:
1. ABSICHT: Was fragt der Nutzer tatsächlich? (Gesundheitsbedenken, Wachstumsfrage, Datenanfrage, Anleitung)
2. BENÖTIGTE DATEN: Welche Tools sollte ich aufrufen für genaue, aktuelle Informationen?
3. ANALYSE: Was sind die möglichen Interpretationen der Daten?
4. EMPFEHLUNGEN: Welche spezifischen, umsetzbaren Schritte sollte der Nutzer unternehmen?
5. NACHFOLGEND: Welche zusätzlichen Fragen oder Aktionen würden dem Nutzer helfen?`,

                toolStrategy: `TOOL-NUTZUNGSSTRATEGIE - Tools aggressiv nutzen für datenbasierte Antworten:

FÜR GESUNDHEITSFRAGEN:
- 'animal-details' für spezifische Kälber mit besorgniserregenden Werten aufrufen
- 'treatment-history' prüfen für vergangene Behandlungen
- 'disease-overview' für Wisconsin- und California-Scores über die Herde nutzen
- 'health-diagnosis-assist' für komplexe Gesundheitsbewertungen nutzen

FÜR KÄLBERWACHSTUM & LEBENSZYKLUS-FRAGEN:
- 'calf-lifecycle-overview' für Stadienverteilung in der Herde aufrufen
- 'weight-growth-analysis' für ADG-Daten und Wachstumstrends
- 'birth-history' für Geburts-/Ankunftsaufzeichnungen und Lebenszyklus-Übergänge prüfen
- 'vaccination-status' für Impf-Compliance prüfen

FÜR LEISTUNGSANALYSE:
- 'calf-health-kpis' für umfassende Gesundheits-KPIs (Mortalität, ADG, BRD-Rate, Durchfallrate)
- 'farm-kpi-overview' für aktuelle Kennzahlen nutzen
- 'farm-kpi-trends' mit Datumsbereichen für Veränderungsanalyse
- 'comparative-analysis' für Gruppen- oder Periodenvergleiche

FÜR FEHLERBEHEBUNG:
- 'bionode-status' für Geräteverbindung prüfen
- 'bionode-analysis' für detaillierte Diagnose aufrufen

FÜR ANLEITUNGSFRAGEN:
- IMMER zuerst 'search-knowledge-base' aufrufen für genaue Anleitung
- Mit relevanten Daten-Tools für Kontext ergänzen

WICHTIG: Mehrere Tools verketten wenn umfassende Antwort erforderlich. Niemals auf Datenfragen antworten ohne die tatsächlichen Daten zu prüfen.`,

                responseGuidelines: `ANTWORTSTRUKTUR - Antworten für Klarheit und Aktion formatieren:

1. SITUATIONSZUSAMMENFASSUNG (1-2 Sätze): Was zeigen die Daten? Was ist die Kernaussage?
2. INTERPRETATION: Was bedeutet das betrieblich? Warum ist es wichtig?
3. EMPFEHLUNGEN: Spezifische, priorisierte Maßnahmen mit klaren nächsten Schritten
   - Prioritätsindikatoren wenn hilfreich: Kritisch/Hoch/Mittel/Niedrig
   - Spezifische Tier-IDs, Daten oder Mengen wenn relevant
4. ÜBERLEGUNGEN: Vorbehalte, Abhängigkeiten oder alternative Ansätze notieren
5. NACHFOLGENDE AKTIONEN: 2-3 kontextbezogene nächste Aktionen oder Fragen vorschlagen

QUALITÄTSSTANDARDS:
- Mit den kritischsten Informationen beginnen
- Spezifische Zahlen, Tier-IDs und Daten verwenden—keine vagen Verweise
- Erklären WARUM eine Aktion empfohlen wird, nicht nur WAS zu tun ist
- Unsicherheit anerkennen wenn Daten unvollständig oder mehrdeutig sind
- Niemals generische Ratschläge geben wenn spezifische Anleitung basierend auf Betriebsdaten möglich
- Bei Gesundheitsbedenken die auf akute Erkrankung hindeuten: Immer tierärztliche Beratung empfehlen
- Bei Medikamentenfragen: An Wartezeiten und Dosierungsverifizierung mit Tierarzt erinnern`
        }
};

/**
 * Builds the enhanced persona section of the prompt
 */
function buildPersonaSection(language, personaName, personaTitle) {
        const isGerman = isGermanLanguage(language);
        const persona = isGerman ? EXPERT_PERSONA.de : EXPERT_PERSONA.en;

        // Use custom name/title if provided, but keep the expertise content
        const identityWithCustomName = persona.identity.replace(
                /You are Lisa|Du bist Lisa/,
                isGerman ? `Du bist ${personaName}` : `You are ${personaName}`
        );

        return identityWithCustomName;
}

/**
 * Builds the reasoning framework section
 */
function buildReasoningSection(language) {
        const isGerman = isGermanLanguage(language);
        return isGerman ? EXPERT_PERSONA.de.reasoning : EXPERT_PERSONA.en.reasoning;
}

/**
 * Builds the tool strategy section
 */
function buildToolStrategySection(language) {
        const isGerman = isGermanLanguage(language);
        return isGerman ? EXPERT_PERSONA.de.toolStrategy : EXPERT_PERSONA.en.toolStrategy;
}

/**
 * Builds the response guidelines section
 */
function buildResponseGuidelinesSection(language) {
        const isGerman = isGermanLanguage(language);
        return isGerman ? EXPERT_PERSONA.de.responseGuidelines : EXPERT_PERSONA.en.responseGuidelines;
}

function formatSuspiciousAnimals(animals = []) {
        if (!animals.length) {
                return "None";
        }

        return animals
                .slice(0, 10)
                .map((animal) => {
                        const parts = [animal.id || animal.mac || "unknown"];
                        if (typeof animal.healthScore === "number") {
                                parts.push(`score ${animal.healthScore}`);
                        }
                        if (animal.group && animal.group !== "Unassigned") {
                                parts.push(`group ${animal.group}`);
                        }
                        if (animal.location && animal.location !== "-") {
                                parts.push(`pen ${animal.location}`);
                        }
                        return parts.join(", ");
                })
                .join(" | ");
}

function formatAlerts(alerts = []) {
        if (!alerts.length) {
                return "None";
        }

        return alerts.map((alert) => `${alert.message} (${alert.severity})`).join("; ");
}

function formatOperationsChecklist({ treatmentStats, predictionStats }) {
        const sections = [];

        if (treatmentStats.overdue.length) {
                sections.push(`Overdue treatments: ${treatmentStats.overdue.length}`);
        }
        if (treatmentStats.dueToday.length) {
                sections.push(`Treatments for today: ${treatmentStats.dueToday.length}`);
        }
        if (predictionStats.health.length) {
                sections.push(`Health predictions: ${predictionStats.health.length}`);
        }
        if (predictionStats.birth.length) {
                sections.push(`Birth predictions: ${predictionStats.birth.length}`);
        }

        if (!sections.length) {
                sections.push("No urgent operational blockers recorded.");
        }

        return sections.join("\n- ");
}

function formatProductionHighlights(productionStats = {}) {
        const lines = [];

        if (typeof productionStats.totalBirths === "number") {
                lines.push(
                        `Birth/arrival records: ${productionStats.totalBirths || 0} (30d: ${productionStats.recentBirths || 0})`
                );
        }

        const birthMetrics = productionStats.birthMetrics || {};
        if (typeof birthMetrics.averageLiving === "number" || typeof birthMetrics.averageDead === "number") {
                const livingText = typeof birthMetrics.averageLiving === "number" ? `avg live ${birthMetrics.averageLiving}` : null;
                const deadText = typeof birthMetrics.averageDead === "number" ? `avg stillborn ${birthMetrics.averageDead}` : null;
                const joined = [livingText, deadText].filter(Boolean).join(", ");
                if (joined) {
                        lines.push(joined);
                }
        }

        if (typeof birthMetrics.assistedRate === "number" && productionStats.totalBirths) {
                const percent = Math.round(birthMetrics.assistedRate * 100);
                if (!Number.isNaN(percent)) {
                        lines.push(`Assisted calvings: ${percent}% of records`);
                }
        }

        if (Array.isArray(productionStats.topBirthAnimals) && productionStats.topBirthAnimals.length) {
                const topBirthSummary = productionStats.topBirthAnimals
                        .map((entry) => `${entry.id} (${entry.births})`)
                        .slice(0, 3)
                        .join(", ");
                lines.push(`Most active dams: ${topBirthSummary}`);
        }

        if (!lines.length) {
                return "No production data recorded.";
        }

        return lines.join("\n- ");
}

/**
 * Builds the system prompt for Gemini 2.5 Pro in the Farm Manager agent
 * @param {Object} userContext - Farm data context (animals, treatments, predictions, etc.)
 * @param {Object} options - Options including language, conversationContext, and settings
 * @returns {Promise<string>} System prompt optimized for Gemini 2.5 Pro
 */
export async function buildFarmManagerPrompt(userContext = {}, options = {}) {
        const { language = "en", conversationContext = "", conversationSummary = null } = options;
        const animals = userContext?.animals?.raw || userContext?.animals?.list || [];
        const treatments = userContext?.treatments?.raw || userContext?.treatments?.list || [];
        const predictions = userContext?.predictions?.raw || userContext?.predictions?.list || [];
        const nodes = userContext?.devices?.raw || userContext?.devices?.list || [];
        const sensors = userContext?.sensors?.raw || userContext?.sensors?.list || [];
        const births = userContext?.production?.births || [];
        const workItems = userContext?.workPlanner?.raw || userContext?.workPlanner?.list || [];

        const animalStats = analyzeAnimals(animals);
        const treatmentStats = analyzeTreatments(treatments);
        const predictionStats = analyzePredictions(predictions);
        const deviceStats = analyzeDevices(nodes);
        const sensorStats = analyzeSensors(sensors);
        const productionStats = analyzeProduction(births, []);
        const workPlannerStats = analyzeWorkPlanner(workItems);
        const alerts = buildOperationalAlerts({ animalStats, treatmentStats, predictionStats });
        const fragments = buildContextFragments({ animalStats, treatmentStats, predictionStats, productionStats, alerts });

        const suspiciousSummary = formatSuspiciousAnimals(animalStats.suspicious);
        const alertSummary = formatAlerts(alerts);
        const operationsChecklist = formatOperationsChecklist({ treatmentStats, predictionStats });

        const contextSummary = userContext?.summary || fragments.join(" | ");

        // Get settings or use defaults
        const settings = options?.settings || DEFAULT_FARM_MANAGER_SETTINGS;
        const knowledgeBaseSettings = settings.knowledgeBase || DEFAULT_FARM_MANAGER_SETTINGS.knowledgeBase;

        // Retrieve relevant knowledge sections based on conversation context
        let relevantKnowledgeSections = getDefaultKnowledgeSections(knowledgeBaseSettings);
        if (conversationContext && conversationContext.trim().length > 0) {
                try {
                        const retrieved = await retrieveRelevantKnowledge(conversationContext, {
                                maxResults: 3,
                                enabledSections: knowledgeBaseSettings.enabledSections,
                        });
                        relevantKnowledgeSections = [...new Set([...relevantKnowledgeSections, ...retrieved])];
                } catch (error) {
                        console.warn("[promptBuilder][buildFarmManagerPrompt] - Error retrieving knowledge:", error);
                        // Fallback to defaults on error
                }
        }

        // Get persona from settings, fallback to defaults based on language
        const personaSettings = settings.persona || DEFAULT_FARM_MANAGER_SETTINGS.persona;
        const personaName = personaSettings.name || (isGermanLanguage(language) ? "Lisa" : "Lisa");
        const personaTitle = personaSettings.title || (isGermanLanguage(language) ? "digitale Betriebsleiterin" : "digital farm manager");

        const deviceStatus = deviceStats.total > 0 
                ? `BioNodes: ${deviceStats.total} total (${deviceStats.connected} connected, ${deviceStats.disconnected} disconnected - ${deviceStats.healthy} healthy, ${deviceStats.warning} warning, ${deviceStats.offline} offline)`
                : "No BioNodes registered";

        const sensorSummary = sensorStats.total > 0
                ? `Sensors: ${sensorStats.total} total (${sensorStats.active} active) - ${Object.entries(sensorStats.byType).map(([type, count]) => `${type}: ${count}`).join(", ")}`
                : "No sensors registered";

        const productionSummary = productionStats.totalBirths
                ? `Production records: ${productionStats.totalBirths || 0} birth/arrival records (${productionStats.recentBirths || 0} last 30d)`
                : "No production data recorded";

        const workPlannerSummary = workPlannerStats.total > 0
                ? `Work planner: ${workPlannerStats.total} tasks (${workPlannerStats.pending} pending, ${workPlannerStats.inProgress} in progress, ${workPlannerStats.dueToday} due today, ${workPlannerStats.overdue} overdue)`
                : "No work items";

        // Filter knowledge sections based on enabled settings
        const enabledSections = relevantKnowledgeSections.filter(
                (sectionKey) => knowledgeBaseSettings.enabledSections[sectionKey] !== false
        );

        const productionHighlights = formatProductionHighlights(productionStats);

        // Build enhanced prompt sections
        const personaSection = buildPersonaSection(language, personaName, personaTitle);
        const reasoningSection = buildReasoningSection(language);
        const toolStrategySection = buildToolStrategySection(language);
        const responseGuidelinesSection = buildResponseGuidelinesSection(language);

        const isGerman = isGermanLanguage(language);

        return formatBrandCopy(`${personaSection}

---

${isGerman ? "AKTUELLE BETRIEBSDATEN" : "CURRENT FARM DATA"}:
- ${isGerman ? "Herdengröße" : "Herd size"}: ${animalStats.total}
- ${isGerman ? "Bestätigte Tierprofile" : "Confirmed animal profiles"}: ${JSON.stringify(animalStats.lifecycleProfiles || {})}
- ${isGerman ? "Kühe, Mutterkühe und Färsen nicht als Kälber oder pauschal als Masttiere einordnen. SchemaVersion 2 liefert zunächst nur beschreibende Schattenauswertungen. Keine Diagnose, Brunst oder Kalbung aus unvalidierten Modellen ableiten; bestätigte Ereignisse und Messungen verwenden." : "Apply context per animal: cows, suckler cows and heifers are not rearing calves or automatically feedlot animals. SchemaVersion 2 provides descriptive shadow analysis only. Do not infer diagnosis, heat or calving from unvalidated models; use confirmed events and measurements."}
- ${isGerman ? "Durchschnittlicher Gesundheitswert" : "Average health score"}: ${typeof animalStats.averageHealthScore === "number" ? animalStats.averageHealthScore : (isGerman ? "unbekannt" : "unknown")} ${isGerman ? "(auch Sensor Trend Index genannt)" : "(also called Sensor Trend Index)"}
- ${isGerman ? "Verdächtige Tiere" : "Suspicious animals"}: ${animalStats.suspiciousCount}
- ${isGerman ? "Gruppenverteilung" : "Group breakdown"}: ${animalStats.groups.map((group) => `${group.name} (${group.count})`).join(", ") || (isGerman ? "keine" : "none")}
- ${isGerman ? "Behandlungen (aktiv/überfällig/heute)" : "Treatments (active/overdue/today)"}: ${treatmentStats.active.length}/${treatmentStats.overdue.length}/${treatmentStats.dueToday.length}
- ${isGerman ? "Vorhersagen (Gesundheit/Geburt)" : "Predictions (health/birth)"}: ${predictionStats.health.length}/${predictionStats.birth.length}
- ${deviceStatus}
- ${sensorSummary}
- ${productionSummary}
- ${workPlannerSummary}

${isGerman ? "PRIORITÄTSALARME" : "PRIORITY ALERTS"}: ${alertSummary}

${isGerman ? "VERDÄCHTIGE TIERE" : "SUSPICIOUS ANIMALS"}: ${suspiciousSummary}

${isGerman ? "OPERATIONS-CHECKLISTE" : "OPERATIONS CHECKLIST"}:
- ${operationsChecklist}

${isGerman ? "PRODUKTIONSEINBLICKE" : "PRODUCTION INSIGHTS"}:
- ${productionHighlights}

---

${reasoningSection}

---

${toolStrategySection}

---

${responseGuidelinesSection}

---

${isGerman ? "AKTUELLE BETRIEBSZUSAMMENFASSUNG" : "CURRENT FARM SUMMARY"}: ${contextSummary}${conversationSummary ? `

---

${conversationSummary}` : ""}${formatKnowledgeBase(language, enabledSections)}`);
}
